import { useSnackbar } from "notistack";
import { useState } from "react";
import { Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  ExtensionOperationContext,
  useCallExtensionOperationMutation,
  useGetExtensionsOperationsQuery,
} from "../../../graphql/generated";
import { matchesFilter } from "../../../utils/extensions/filterEvaluation";
import { testIds } from "./ExtensionOperationButtons.testIds";

export interface ExtensionOperationButtonsProps {
  context: ExtensionOperationContext;
  record: Record<string, any>;
  recordId: string;
}

/**
 * Renders extension operation buttons for a given context and record.
 * Queries available extension operations, evaluates filters against the record,
 * and renders a LoadingButton for each matching operation.
 *
 * Used on detail pages (e.g., Trip Details) where SmartLynksTable is not available.
 */
function ExtensionOperationButtons({
  context,
  record,
  recordId,
}: ExtensionOperationButtonsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [loadingKey, setLoadingKey] = useState<string | null>(null);

  const { data } = useGetExtensionsOperationsQuery({ context });
  const extensionOperations = data?.extensionsOperations ?? [];

  const callOperationMutation = useCallExtensionOperationMutation();

  const visibleOperations = extensionOperations.filter((op) =>
    matchesFilter(record, op.filter)
  );

  if (visibleOperations.length === 0) {
    return null;
  }

  const handleClick = async (operation: (typeof visibleOperations)[number]) => {
    setLoadingKey(operation.key);
    try {
      const response = await callOperationMutation.mutateAsync({
        extensionId: operation.extensionId,
        operationKey: operation.key,
        recordId,
      });
      if (response.callExtensionOperation.message) {
        enqueueSnackbar(response.callExtensionOperation.message);
      }
      if (response.callExtensionOperation.redirect) {
        if (response.callExtensionOperation.openNewWindow) {
          window.open(
            response.callExtensionOperation.redirect,
            `${recordId}::${operation.extensionId}::${operation.key}`,
            "scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=992,height=680,left=100,top=100"
          );
        } else {
          window.location.href = response.callExtensionOperation.redirect;
        }
      }
    } catch (error) {
      const errorMessage =
        (error as Error).message || "Unknown error occurred";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <Stack direction="row" spacing={1}>
      {visibleOperations.map((operation) => (
        <LoadingButton data-testid={`${testIds.extensionOperationButtonsRun}-${operation.key}`}
          key={operation.key}
          variant="outlined"
          color="lightPrimary"
          size="large"
          loading={loadingKey === operation.key}
          onClick={() => handleClick(operation)}
          startIcon={
            operation.icon ? (
              <img
                src={operation.icon}
                alt={operation.description}
                style={{ width: 20, height: 20 }}
              />
            ) : undefined
          }
        >
          {operation.name}
        </LoadingButton>
      ))}
    </Stack>
  );
}

export default ExtensionOperationButtons;
